import * as React from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LoginIcon from '@mui/icons-material/Login';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import {
    deleteUserPrivateClub,
    getPrivateClubTemplates,
    getUserPrivateClub,
    getUserPrivateClubCurrentRoundMatches,
    getUserPrivateClubGrabbedBets,
    getUserPrivateClubLeaderboard,
    getUserPrivateClubPlacedBetsForMatch,
    getUserPrivateClubs,
    joinUserPrivateClub,
    placeUserPrivateClubPick,
    updateUserPrivateClub,
} from '../Api';
import PrivateClubMatchSummary from './PrivateClubMatchSummary';
import PrivateClubDto, { PrivateClubBetDto, PrivateClubBetSectionDto, PrivateClubLeaderboardEntryDto, PrivateClubPlacedBetDto, PrivateClubTemplateDto } from '../models/dtos/PrivateClubDto';
import {
    PrivateClubCurrentRoundAdminDto,
    PrivateClubCurrentRoundMatchAdminDto,
} from '../models/dtos/PrivateClubAdminDto';
import PrivateClubTestAsUserDialog from './PrivateClubTestAsUserDialog';
import AccountMainInfoDto from '../models/dtos/AccountMainInfoDto';
import CreatePrivateClubDialog from './CreatePrivateClubDialog';

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function formatBetcoins(value: number) {
    return value.toLocaleString();
}

function feeBankLabel(club: PrivateClubDto) {
    if (club.rewardMode !== 'BETCOIN') return 'Social';
    return `${formatBetcoins(club.entryFeeBetcoins ?? 0)} / ${formatBetcoins(club.bankBetcoins)}`;
}

function totalPlayableMatches(template?: PrivateClubTemplateDto) {
    return template?.rounds.reduce((total, round) => {
        return total + round.matches.filter(match => match.settlementStatus !== 'EXCLUDED').length;
    }, 0) ?? 0;
}

const privateClubBetGroupFallbackNames: Record<number, string> = {
    1: '1x2',
    5: 'Total over',
    6: 'Total under',
    7: 'Both teams to score',
    46: 'Player to score',
    47: 'Player not to score',
};

function betDisplayName(bet: PrivateClubBetDto) {
    return bet.bet_name?.trim() || 'Bet';
}

export default function UserPrivateClubsTable({ userId, user }: { userId: number; user?: AccountMainInfoDto | null }) {
    const navigate = useNavigate();
    const [clubs, setClubs] = React.useState<PrivateClubDto[]>([]);
    const [templates, setTemplates] = React.useState<PrivateClubTemplateDto[]>([]);
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [joinDialogOpen, setJoinDialogOpen] = React.useState(false);
    const [joinInviteToken, setJoinInviteToken] = React.useState('');
    const [editingClub, setEditingClub] = React.useState<PrivateClubDto | null>(null);
    const [testingClub, setTestingClub] = React.useState<PrivateClubDto | null>(null);
    const [selectedClub, setSelectedClub] = React.useState<PrivateClubDto | null>(null);
    const [grabbedBetSectionsByKey, setGrabbedBetSectionsByKey] = React.useState<Record<string, PrivateClubBetSectionDto[]>>({});
    const [selectedGrabbedBetByKey, setSelectedGrabbedBetByKey] = React.useState<Record<string, string>>({});
    const [currentRound, setCurrentRound] = React.useState<PrivateClubCurrentRoundAdminDto | null>(null);
    const [currentRoundMatches, setCurrentRoundMatches] = React.useState<PrivateClubCurrentRoundMatchAdminDto[]>([]);
    const [leaderboard, setLeaderboard] = React.useState<PrivateClubLeaderboardEntryDto[]>([]);
    const [placedBetsByKey, setPlacedBetsByKey] = React.useState<Record<string, PrivateClubPlacedBetDto[]>>({});
    const [currentRoundLoading, setCurrentRoundLoading] = React.useState(false);
    const [actionLoadingKey, setActionLoadingKey] = React.useState<string | null>(null);
    const [editForm, setEditForm] = React.useState({
        name: '',
        rulesText: '',
    });
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [message, setMessage] = React.useState<string | null>(null);

    const templateMap = React.useMemo(() => {
        return new Map(templates.map(template => [template.id, template]));
    }, [templates]);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        const [clubRows, templateRows] = await Promise.all([
            getUserPrivateClubs(userId),
            getPrivateClubTemplates(),
        ]);
        setClubs(clubRows ?? []);
        setTemplates(templateRows ?? []);
        setLoading(false);
        if (!clubRows) setError('Failed to load private clubs.');
        if (!templateRows) setError(prev => prev ?? 'Failed to load private club templates.');
    }, [userId]);

    React.useEffect(() => {
        load();
    }, [load]);

    React.useEffect(() => {
        if (!selectedClub) return;

        let cancelled = false;
        setCurrentRoundLoading(true);
        Promise.all([
            getUserPrivateClubCurrentRoundMatches(userId, selectedClub.id),
            getUserPrivateClubLeaderboard(userId, selectedClub.id),
        ]).then(([roundMatchesResponse, leaderboardResponse]) => {
            if (cancelled) return;
            setCurrentRound(roundMatchesResponse?.round ?? null);
            setCurrentRoundMatches(roundMatchesResponse?.matches ?? []);
            setLeaderboard(leaderboardResponse?.items ?? []);
            if (!roundMatchesResponse) setError(prev => prev ?? 'Failed to load current round matches.');
            if (!leaderboardResponse) setError(prev => prev ?? 'Failed to load private club leaderboard.');
        }).finally(() => {
            if (!cancelled) setCurrentRoundLoading(false);
        });

        return () => { cancelled = true; };
    }, [selectedClub, userId]);

    React.useEffect(() => {
        if (!selectedClub || !currentRound || currentRoundMatches.length === 0) {
            setPlacedBetsByKey({});
            return;
        }

        let cancelled = false;
        Promise.all(currentRoundMatches.map(item => {
            const key = `${currentRound.id}:${item.matchId}`;
            return getUserPrivateClubPlacedBetsForMatch(userId, selectedClub.id, currentRound.id, item.matchId)
                .then(response => [key, response?.placed_bets ?? []] as const)
                .catch(() => [key, [] as PrivateClubPlacedBetDto[]] as const);
        })).then(results => {
            if (cancelled) return;
            setPlacedBetsByKey(prev => {
                const next = { ...prev };
                results.forEach(([key, items]) => {
                    next[key] = items;
                });
                return next;
            });
        });

        return () => { cancelled = true; };
    }, [currentRound, currentRoundMatches, selectedClub, userId]);

    const openCreate = () => {
        setError(null);
        setMessage(null);
        setCreateDialogOpen(true);
    };

    const openJoin = () => {
        setError(null);
        setMessage(null);
        setJoinInviteToken('');
        setJoinDialogOpen(true);
    };

    const joinClub = async () => {
        setError(null);
        setMessage(null);
        if (!joinInviteToken.trim()) {
            setError('Invite token is required.');
            return;
        }
        const result = await joinUserPrivateClub(userId, { inviteToken: joinInviteToken.trim() });
        if (!result.ok) {
            setError(result.error ?? 'Private club was not joined.');
            return;
        }
        setJoinDialogOpen(false);
        setMessage('Private club joined.');
        await load();
    };

    const openEdit = (club: PrivateClubDto) => {
        setEditingClub(club);
        setEditForm({
            name: club.name,
            rulesText: club.rulesText ?? '',
        });
    };

    const openClub = async (club: PrivateClubDto) => {
        setError(null);
        setMessage(null);
        setTestingClub(club);
    };

    const saveEdit = async () => {
        if (!editingClub) return;
        setError(null);
        setMessage(null);
        const result = await updateUserPrivateClub(userId, editingClub.id, {
            name: editForm.name.trim(),
            rulesText: editForm.rulesText.trim() || '',
        });
        if (!result.ok) {
            setError(result.error ?? 'Private club was not updated.');
            return;
        }
        setEditingClub(null);
        setMessage('Private club updated.');
        await load();
    };

    const deleteClub = async (club: PrivateClubDto) => {
        if (!window.confirm(`Dismiss private club "${club.name}" for user ${userId}?`)) return;
        setError(null);
        setMessage(null);
        const result = await deleteUserPrivateClub(userId, club.id);
        if (!result.ok) {
            setError(result.error ?? 'Private club was not dismissed.');
            return;
        }
        setMessage('Private club dismissed.');
        await load();
    };

    const pickKey = (roundId: string, matchId: number) => `${roundId}:${matchId}`;

    const loadGrabbedBets = async (
        club: PrivateClubDto,
        round: PrivateClubCurrentRoundAdminDto,
        match: PrivateClubCurrentRoundMatchAdminDto,
    ) => {
        const key = pickKey(round.id, match.matchId);
        setActionLoadingKey(`load:${key}`);
        setError(null);
        const result = await getUserPrivateClubGrabbedBets(userId, club.id, round.id, match.matchId);
        setActionLoadingKey(null);
        if (!result) {
            setError('Failed to load grabbed bets for this round match.');
            return;
        }
        setGrabbedBetSectionsByKey(prev => ({ ...prev, [key]: result.sections }));
        const firstAvailableBet = result.sections
            .flatMap(section => section.groups)
            .flatMap(group => group.bets)
            .find(bet => bet.is_available);
        setSelectedGrabbedBetByKey(prev => ({ ...prev, [key]: firstAvailableBet ? String(firstAvailableBet.grabbed_bet_id) : '' }));
    };

    const placePick = async (
        club: PrivateClubDto,
        round: PrivateClubCurrentRoundAdminDto,
        match: PrivateClubCurrentRoundMatchAdminDto,
    ) => {
        const key = pickKey(round.id, match.matchId);
        const grabbedBetId = Number(selectedGrabbedBetByKey[key]);
        if (!grabbedBetId) {
            setError('Grabbed bet is required.');
            return;
        }
        setActionLoadingKey(`pick:${key}`);
        setError(null);
        setMessage(null);
        const result = await placeUserPrivateClubPick(userId, club.id, {
            roundId: round.id,
            matchId: match.matchId,
            grabbedBetId,
        });
        setActionLoadingKey(null);
        if (!result.ok) {
            setError(result.error ?? 'Private club pick was not placed.');
            return;
        }
        setMessage('Private club pick placed.');
        if (result.data?.[0]) {
            setPlacedBetsByKey(prev => ({ ...prev, [key]: [result.data![0]] }));
        }
        const loadedClub = await getUserPrivateClub(userId, club.id);
        if (loadedClub) setSelectedClub(loadedClub);
        await load();
    };

    return (
        <Stack gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {message && <Alert severity="success">{message}</Alert>}

            <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Typography variant="h6">Private Clubs</Typography>
                <Stack direction="row" gap={1} alignItems="center">
                    <Button
                        variant="outlined"
                        startIcon={<LoginIcon />}
                        onClick={openJoin}
                        disabled={loading}
                    >
                        Join
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                        disabled={loading || templates.every(template => template.status !== 'ACTIVE')}
                    >
                        Create
                    </Button>
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton onClick={load} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Mode</TableCell>
                        <TableCell>Fee / Bank</TableCell>
                        <TableCell>Members</TableCell>
                        <TableCell>Picks</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={10} align="center">Loading private clubs...</TableCell>
                        </TableRow>
                    )}
                    {!loading && clubs.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={10} align="center">No private clubs found</TableCell>
                        </TableRow>
                    )}
                    {!loading && clubs.map(club => {
                        const template = templateMap.get(club.templateId);
                        return (
                            <TableRow key={club.id}>
                                <TableCell>
                                    <Stack spacing={0.25}>
                                        <Typography variant="body2">{club.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{club.id}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={0.25}>
                                        <span>{template?.title ?? club.templateId}</span>
                                        {template && <Typography variant="caption" color="text.secondary">{template.id}</Typography>}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={club.status} color={club.status === 'ACTIVE' ? 'success' : 'default'} />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={club.rewardMode}
                                        color={club.rewardMode === 'BETCOIN' ? 'primary' : 'default'}
                                    />
                                </TableCell>
                                <TableCell>{feeBankLabel(club)}</TableCell>
                                <TableCell>{club.membersCount}</TableCell>
                                <TableCell>{club.placedBetsCount} / {totalPlayableMatches(template)}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={club.role ?? '-'}
                                        color={club.role === 'ADMIN' ? 'primary' : club.role === 'MEMBER' ? 'default' : 'warning'}
                                        variant={club.role === 'MEMBER' ? 'outlined' : 'filled'}
                                    />
                                </TableCell>
                                <TableCell>{formatDate(club.updatedAt)}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Details">
                                        <IconButton size="small" onClick={() => navigate(`/users/${userId}/private-clubs/${club.id}`)}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Test as user">
                                        <IconButton size="small" onClick={() => openClub(club)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => openEdit(club)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Dismiss">
                                        <IconButton size="small" color="error" onClick={() => deleteClub(club)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Dialog open={editingClub != null} onClose={() => setEditingClub(null)} fullWidth maxWidth="sm">
                <DialogTitle>Edit private club</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Club name"
                            value={editForm.name}
                            onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Rules"
                            value={editForm.rulesText}
                            onChange={e => setEditForm(prev => ({ ...prev, rulesText: e.target.value }))}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingClub(null)}>Cancel</Button>
                    <Button variant="contained" onClick={saveEdit}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Join private club as user {userId}</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Invite token"
                            value={joinInviteToken}
                            onChange={e => setJoinInviteToken(e.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase())}
                            inputProps={{ maxLength: 6 }}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setJoinDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={joinClub}>Join</Button>
                </DialogActions>
            </Dialog>

            <CreatePrivateClubDialog
                open={createDialogOpen}
                templates={templates}
                initialUser={user ?? null}
                onClose={() => setCreateDialogOpen(false)}
                onCreated={async club => {
                    setMessage(`Private club "${club.name}" created for user ${club.createdBy}.`);
                    await load();
                }}
            />

            <PrivateClubTestAsUserDialog
                open={testingClub != null}
                userId={userId}
                club={testingClub}
                templates={templates}
                onClose={() => setTestingClub(null)}
                onClubUpdated={updatedClub => {
                    setTestingClub(updatedClub);
                    setClubs(prev => prev.map(club => club.id === updatedClub.id ? updatedClub : club));
                }}
            />

            <Dialog open={selectedClub != null} onClose={() => setSelectedClub(null)} fullWidth maxWidth="lg">
                <DialogTitle>Test private club as user {userId}</DialogTitle>
                <DialogContent>
                    {selectedClub && (() => {
                        const template = templateMap.get(selectedClub.templateId);
                        return (
                            <Stack gap={2} sx={{ mt: 1 }}>
                                <Stack gap={0.5}>
                                    <Typography variant="subtitle1">{selectedClub.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Template: {template?.title ?? selectedClub.templateId} | Status: {selectedClub.status}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Members: {selectedClub.membersCount} | Placed picks: {selectedClub.placedBetsCount} / {totalPlayableMatches(template)}
                                    </Typography>
                                </Stack>
                                <Divider />
                                {!template && <Alert severity="warning">Template data is not loaded for this club.</Alert>}
                                {currentRoundLoading && <Typography variant="body2" color="text.secondary">Loading current round...</Typography>}
                                {!currentRoundLoading && !currentRound && (
                                    <Alert severity="info">There is no current round with available matches.</Alert>
                                )}
                                {currentRound && (
                                    <Stack key={currentRound.id} gap={1} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.5 }}>
                                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                            <Typography variant="subtitle2">Round {currentRound.index}</Typography>
                                            <Chip size="small" label={currentRound.status} />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(currentRound.startsAt)} - {formatDate(currentRound.endsAt)}
                                            </Typography>
                                        </Stack>
                                        {currentRoundMatches.length === 0 && <Typography variant="body2" color="text.secondary">No matches.</Typography>}
                                        {currentRoundMatches.map(item => {
                                            const match = item;
                                            const key = pickKey(currentRound.id, match.matchId);
                                            const sections = grabbedBetSectionsByKey[key];
                                            const betsCount = sections?.reduce((count, section) => (
                                                count + section.groups.reduce((groupCount, group) => groupCount + group.bets.length, 0)
                                            ), 0) ?? 0;
                                            const allowedGroupNames = match.allowedBetTypeGroupIds
                                                .map(groupId => privateClubBetGroupFallbackNames[groupId] ?? null)
                                                .filter(Boolean)
                                                .join(', ');
                                            const loadingBets = actionLoadingKey === `load:${key}`;
                                            const placingPick = actionLoadingKey === `pick:${key}`;
                                            const placedPick = placedBetsByKey[key]?.[0] ?? null;
                                            return (
                                                <Stack key={key} gap={1} sx={{ borderTop: '1px solid #eee', pt: 1 }}>
                                                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                                                        <PrivateClubMatchSummary
                                                            matchId={match.matchId}
                                                            match={item.match_info ?? null}
                                                            link
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={item.settlementStatus}
                                                            color={item.settlementStatus === 'SETTLED' ? 'success' : item.settlementStatus === 'PENDING' ? 'warning' : 'default'}
                                                        />
                                                        {placedPick && (
                                                            <Chip
                                                                size="small"
                                                                label="Picked"
                                                                color="success"
                                                            />
                                                        )}
                                                    </Stack>
                                                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ pl: { xs: 0, sm: 4.5 } }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Allowed groups: {allowedGroupNames || '-'}
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => loadGrabbedBets(selectedClub, currentRound, match)}
                                                            disabled={loadingBets || item.settlementStatus === 'EXCLUDED'}
                                                        >
                                                            {loadingBets ? 'Loading...' : 'Show bets'}
                                                        </Button>
                                                    </Stack>
                                                    {sections && (
                                                        <Stack gap={1}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {betsCount} allowed bets
                                                            </Typography>
                                                            {betsCount === 0 && (
                                                                <Typography variant="body2" color="text.secondary">No allowed bets</Typography>
                                                            )}
                                                            {sections.map(section => section.groups.map(group => (
                                                                <Stack key={`${section.variety.id}:${group.group.id}`} gap={0.75}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {group.group.name || privateClubBetGroupFallbackNames[group.group.id] || 'Bet group'}
                                                                    </Typography>
                                                                    <Stack direction="row" gap={1} flexWrap="wrap">
                                                                        {group.bets.map(bet => {
                                                                            const betId = String(bet.grabbed_bet_id);
                                                                            const selected = selectedGrabbedBetByKey[key] === betId;
                                                                            return (
                                                                                <Button
                                                                                    key={bet.grabbed_bet_id}
                                                                                    size="small"
                                                                                    variant={selected ? 'contained' : 'outlined'}
                                                                                    color={selected ? 'primary' : 'inherit'}
                                                                                    disabled={!bet.is_available || Boolean(placedPick)}
                                                                                    onClick={() => setSelectedGrabbedBetByKey(prev => ({ ...prev, [key]: betId }))}
                                                                                    sx={{
                                                                                        justifyContent: 'stretch',
                                                                                        minWidth: 210,
                                                                                        p: 0,
                                                                                        overflow: 'hidden',
                                                                                        textTransform: 'none',
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        component="span"
                                                                                        sx={{
                                                                                            flex: 1,
                                                                                            px: 1,
                                                                                            py: 0.5,
                                                                                            minWidth: 0,
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap',
                                                                                            bgcolor: selected ? 'primary.main' : 'action.hover',
                                                                                        }}
                                                                                    >
                                                                                        {betDisplayName(bet)}
                                                                                    </Box>
                                                                                    <Box
                                                                                        component="span"
                                                                                        sx={{
                                                                                            px: 1,
                                                                                            py: 0.5,
                                                                                            display: 'inline-flex',
                                                                                            gap: 0.75,
                                                                                            alignItems: 'center',
                                                                                            borderLeft: 1,
                                                                                            borderColor: selected ? 'primary.dark' : 'divider',
                                                                                            bgcolor: selected ? 'primary.dark' : 'background.paper',
                                                                                            whiteSpace: 'nowrap',
                                                                                        }}
                                                                                    >
                                                                                        <Box component="span">{bet.grabbed_coeficient}</Box>
                                                                                        <Box component="span" sx={{ fontWeight: 700 }}>{bet.score_points} pts</Box>
                                                                                    </Box>
                                                                                </Button>
                                                                            );
                                                                        })}
                                                                    </Stack>
                                                                </Stack>
                                                            )))}
                                                            <Stack direction="row" justifyContent="flex-end">
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => placePick(selectedClub, currentRound, match)}
                                                                    disabled={placingPick || !selectedGrabbedBetByKey[key] || Boolean(placedPick)}
                                                                >
                                                                    {placingPick ? 'Placing...' : 'Place pick'}
                                                                </Button>
                                                            </Stack>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                )}
                                {leaderboard.length > 0 && (
                                    <Stack gap={0.75}>
                                        <Typography variant="subtitle2">Leaderboard</Typography>
                                        <Stack direction="row" gap={1} flexWrap="wrap">
                                            {leaderboard.slice(0, 5).map(entry => (
                                                <Chip
                                                    key={`${entry.rank}:${entry.user.id}`}
                                                    size="small"
                                                    variant={entry.user.id === userId ? 'filled' : 'outlined'}
                                                    color={entry.user.id === userId ? 'primary' : 'default'}
                                                    label={`#${entry.rank} ${entry.user.name?.trim() || entry.user.id}: ${entry.totalPoints} pts`}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedClub(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
